import * as React from "react"
import { cn } from "@/lib/utils"
import { Edit, Eye, Split } from "lucide-react"

type MarkdownMode = 'edit' | 'preview' | 'split';

interface ModeToggleProps {
  mode: MarkdownMode;
  onModeChange: (mode: MarkdownMode) => void;
  disabled?: boolean;
  className?: string;
  showSplit?: boolean; // Whether to show the split option
}

const ModeToggle = React.forwardRef<
  HTMLDivElement,
  ModeToggleProps
>(({ mode, onModeChange, disabled = false, className, showSplit = false }, ref) => {
  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent, targetMode: MarkdownMode) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!disabled && mode !== targetMode) {
        onModeChange(targetMode);
      }
    }
  };

  const getButtonClasses = (buttonMode: MarkdownMode) => {
    const isActive = mode === buttonMode;
    return cn(
      "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors flex-1",
      "min-h-9 px-3 py-2", // Standard toggle height and padding
      "border-0 border-none outline-none shadow-none", // Remove all borders, outlines, and shadows
      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring", // Focus ring like shadcn
      "disabled:pointer-events-none disabled:opacity-50",
      !disabled && "cursor-pointer",
      isActive
        ? "bg-muted text-foreground" // Active state with muted background but normal text color
        : "bg-transparent text-muted-foreground hover:bg-muted hover:text-muted-foreground" // Inactive state with muted text and hover
    );
  };

  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center w-full bg-background", // Full width, no border
        className
      )}
      role="tablist"
      aria-label="Markdown editing modes"
    >
      {/* Edit Mode Button */}
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'edit'}
        aria-controls="markdown-content"
        data-state={mode === 'edit' ? 'on' : 'off'}
        tabIndex={mode === 'edit' ? 0 : -1}
        disabled={disabled}
        className={getButtonClasses('edit')}
        onClick={() => !disabled && mode !== 'edit' && onModeChange('edit')}
        onKeyDown={(e) => handleKeyDown(e, 'edit')}
        title="Edit markdown source (Ctrl+Shift+E)"
      >
        <Edit className="h-4 w-4" />
        Edit
      </button>

      {/* Split Mode Button - Only show if enabled */}
      {showSplit && (
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'split'}
          aria-controls="markdown-content"
          data-state={mode === 'split' ? 'on' : 'off'}
          tabIndex={mode === 'split' ? 0 : -1}
          disabled={disabled}
          className={getButtonClasses('split')}
          onClick={() => !disabled && mode !== 'split' && onModeChange('split')}
          onKeyDown={(e) => handleKeyDown(e, 'split')}
          title="Split view: edit and preview (Ctrl+Shift+S)"
        >
          <Split className="h-4 w-4" />
          Split
        </button>
      )}

      {/* Preview Mode Button */}
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'preview'}
        aria-controls="markdown-content"
        data-state={mode === 'preview' ? 'on' : 'off'}
        tabIndex={mode === 'preview' ? 0 : -1}
        disabled={disabled}
        className={getButtonClasses('preview')}
        onClick={() => !disabled && mode !== 'preview' && onModeChange('preview')}
        onKeyDown={(e) => handleKeyDown(e, 'preview')}
        title="Preview formatted content (Ctrl+Shift+P)"
      >
        <Eye className="h-4 w-4" />
        Preview
      </button>
    </div>
  );
});

ModeToggle.displayName = "ModeToggle";

export { ModeToggle, type MarkdownMode }; 